import { Box, Button, Flex, Group, Image, Loader, Stack, Text, ThemeIcon } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { IconPhoto, IconUpload, IconX } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import type { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import { extractPaletteFromImage } from 'src/secret-menu/brand-asset-builder/lib/paletteExtractor';
import { lightTint } from 'src/secret-menu/brand-asset-builder/lib/hexColour';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { Logger } from 'src/utils/Logger';
import {
  DEFAULT_BRAND_ASSET_BUILDER_PALETTE,
  type BrandAssetBuilderData,
} from 'src/secret-menu/brand-asset-builder/types';

const MAX_LOGO_SIZE = 5 * 1024 * 1024;

const ACCEPTED_LOGO_MIME_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

export default function UploadLogoStep(props: StepProps<BrandAssetBuilderData>) {
  const { data, setData } = props;
  const [isExtractingPalette, setIsExtractingPalette] = useState(false);
  // Tracks which dropped `File` the in-flight extraction (if any) belongs to. Updated
  // synchronously alongside `data.logoFile` in `handleDrop`/`handleRemove`, so reading it is
  // always consistent with what's currently on screen — unlike reading a variable set inside a
  // `setData` functional updater (which isn't guaranteed to run synchronously with the
  // surrounding code; only the update it produces is guaranteed to apply against fresh state).
  // Used both for the loading indicator and, together with `isMountedRef`, for deciding whether
  // the failure toast below is still relevant.
  const currentLogoFileRef = useRef<File | null>(null);
  // Tracks whether this component is still mounted, purely to decide whether it's safe/sensible
  // to show the failure toast — see the `.catch()` branch below.
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleDrop = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    if (data.logoUrl) {
      URL.revokeObjectURL(data.logoUrl);
    }
    currentLogoFileRef.current = file;
    setData({
      ...data,
      logoFile: file,
      logoUrl: URL.createObjectURL(file),
    });

    setIsExtractingPalette(true);
    extractPaletteFromImage(file)
      .then(extracted => {
        // Guard against a stale extraction (superseded by a later drop, a "Remove", a tenant
        // switch, or a drawer close/reopen) overwriting the CURRENT palette. `prev` here is
        // always the authoritative, up-to-date data at the moment this settles, so comparing it
        // against the `file` this specific extraction was started for is sufficient to detect
        // staleness.
        setData(prev => {
          if (prev.logoFile !== file) {
            return prev;
          }
          return {
            ...prev,
            palette: {
              primary: extracted.primary,
              secondary: extracted.secondary,
              accent: extracted.accent,
              darkText: extracted.darkText,
              background: extracted.lightBackground,
            },
            extractedColours: extracted.extractedColors,
            tableBorderColour: lightTint(extracted.primary),
            tableHeaderColour: extracted.primary,
            bulletColour: extracted.accent,
          };
        });
      })
      .catch(error => {
        Logger.error('Failed to extract a colour palette from the uploaded logo', error);
        setData(prev => {
          if (prev.logoFile !== file) {
            return prev;
          }
          return {
            ...prev,
            palette: DEFAULT_BRAND_ASSET_BUILDER_PALETTE,
            extractedColours: [],
            tableBorderColour: lightTint(DEFAULT_BRAND_ASSET_BUILDER_PALETTE.primary),
            tableHeaderColour: DEFAULT_BRAND_ASSET_BUILDER_PALETTE.primary,
            bulletColour: DEFAULT_BRAND_ASSET_BUILDER_PALETTE.accent,
          };
        });
        // Only show the toast if this step is still mounted with the same logo the failed
        // extraction was for — otherwise it's a confusing message about a now-abandoned upload.
        // Deliberately reads `currentLogoFileRef` (kept in sync synchronously by `handleDrop`/
        // `handleRemove`) rather than a variable set inside the `setData` updater above: that
        // updater isn't guaranteed to run synchronously with the surrounding code, so a variable
        // it sets can't be read reliably immediately after the `setData` call returns.
        if (isMountedRef.current && currentLogoFileRef.current === file) {
          notifications.show({
            title: 'Colour extraction failed',
            message:
              "We couldn't automatically extract a colour palette from this logo. The placeholder palette has been used instead — adjust it manually on the next step.",
            color: 'yellow',
          });
        }
      })
      .finally(() => {
        if (currentLogoFileRef.current === file) {
          setIsExtractingPalette(false);
        }
      });
  };

  const handleRemove = () => {
    if (data.logoUrl) {
      URL.revokeObjectURL(data.logoUrl);
    }
    currentLogoFileRef.current = null;
    setData({ ...data, logoFile: null, logoUrl: null, extractedColours: [] });
  };

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Upload logo"
      description="Upload the tenant's logo. We'll use it to extract a colour palette and place it on the title page."
      canGoForward={data.logoFile !== null && !isExtractingPalette}
      showClientName={false}
      hideProgress
      allowVerticalOverflow
    >
      <Stack gap="lg">
        {data.logoFile && data.logoUrl ? (
          <Flex align="center" gap="md" p="sm" bg="gray.0" style={{ borderRadius: 8 }}>
            <Box
              w={96}
              h={96}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Image src={data.logoUrl} alt="Tenant logo" fit="contain" h={96} />
            </Box>
            <Stack gap={2} flex={1}>
              <Text size="textSm" fw={FONT_WEIGHTS.medium}>
                {data.logoFile.name}
              </Text>
              <Text size="textSm" c="gray.6">
                {(data.logoFile.size / 1024).toFixed(0)} KB
              </Text>
              {isExtractingPalette && (
                <Group gap="xs" mt={4}>
                  <Loader size="xs" />
                  <Text size="xs" c="gray.6">
                    Extracting colour palette…
                  </Text>
                </Group>
              )}
            </Stack>
            <Button variant="subtle" color="red" size="xs" onClick={handleRemove}>
              Remove
            </Button>
          </Flex>
        ) : (
          <Dropzone
            onDrop={handleDrop}
            onReject={() =>
              notifications.show({
                title: 'Rejected',
                message: 'Logo must be an image under 5MB.',
                color: 'red',
              })
            }
            maxSize={MAX_LOGO_SIZE}
            maxFiles={1}
            accept={ACCEPTED_LOGO_MIME_TYPES}
            style={{
              border: '1px dashed var(--mantine-color-gray-4)',
              borderRadius: 12,
              padding: 40,
            }}
          >
            <Flex direction="column" align="center" gap="md">
              <Dropzone.Accept>
                <ThemeIcon variant="light" color="blue" size={56} radius="xl">
                  <IconUpload size={28} />
                </ThemeIcon>
              </Dropzone.Accept>
              <Dropzone.Reject>
                <ThemeIcon variant="light" color="red" size={56} radius="xl">
                  <IconX size={28} />
                </ThemeIcon>
              </Dropzone.Reject>
              <Dropzone.Idle>
                <ThemeIcon variant="light" color="gray" size={56} radius="xl">
                  <IconPhoto size={28} />
                </ThemeIcon>
              </Dropzone.Idle>
              <Text fw={FONT_WEIGHTS.semibold} size="textLg">
                Drop the tenant&apos;s logo here
              </Text>
              <Text c="gray.6" size="textSm">
                or click to browse — PNG, JPG, GIF or WebP, max 5MB
              </Text>
            </Flex>
          </Dropzone>
        )}
      </Stack>
    </DrawerFlowScreenLayout>
  );
}
